import { useMemo } from 'react'
import type { JSX } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import CircleLoaderSvg from '@strata-monorepo/shared-ui/images/icons/CircleLoaderSvg'

export interface CircleLoaderProps {
  percentage?: number
  className?: string
  durationMs?: number
  easing?: string
}

const RADIUS = 36
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const CircleLoader = ({
  percentage = 0,
  className,
  durationMs: _durationMs = 400,
  easing: _easing = 'ease-in-out',
}: CircleLoaderProps): JSX.Element => {
  const { progress, dashOffset } = useMemo(() => {
    const clamped = Math.min(Math.max(percentage, 0), 100)
    return {
      progress: clamped,
      dashOffset: CIRCUMFERENCE * (1 - clamped / 100),
    }
  }, [percentage])

  return (
    <div
      className={cn('strata:inline-flex strata:items-center strata:justify-center', className)}
      role='progressbar'
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
    >
      <div className='strata:relative strata:w-10 strata:h-10 strata:flex strata:items-center strata:justify-center'>
        <div className='strata:w-full strata:h-full strata:absolute strata:top-0 strata:left-0'>
          <CircleLoaderSvg
            radius={RADIUS}
            dashOffset={dashOffset}
            circumference={CIRCUMFERENCE}
            className={cn(
              'strata:w-full strata:h-full strata:transform strata:-rotate-90 strata:transition-[stroke-dashoffset] strata:duration-300 strata:ease-in-out',
              progress > 0 ? 'strata:text-[#63E283]' : 'strata:text-transparent',
              className,
            )}
          />
        </div>
        {progress === 100 && (
          <div className='strata:relative strata:w-full strata:h-full strata:flex strata:items-center strata:justify-center'>
            <Check className='strata:w-5 strata:h-5 strata:text-green-500' />
          </div>
        )}
      </div>
    </div>
  )
}

export default CircleLoader
